// ===== BeamCRM · dados base — LANÇAMENTO: transacionais zerados; catálogo e configurações preservados =====

// `src/dinheiro.js` não tem dependência nenhuma — importá-lo aqui não esbarra na
// regra de imports (o que data.jsx não pode importar é data/api.js e
// data/supabase.js).
import { dinheiro, somarDinheiro } from './dinheiro.js';
import { tipoContribuinte } from './cliente-erp.js';
import { brlExato } from '../supabase/functions/_shared/proposta-documento.js';
import { EMISSORAS, EMISSORAS_BY_ID } from '../supabase/functions/_shared/emissoras.js';
import { AVISO_PROPOSTA, CONDICOES_PADRAO } from '../supabase/functions/_shared/textos-da-proposta.js';

// ----- Funis (dois funis paralelos) -----
const FUNNELS = [
  {
    id: "projetos",
    name: "Projetos",
    short: "Projetos",
    subtitle: "Venda complexa · B2B2C",
    description: "Empresa → Arquiteto → Cliente Final",
    cycle: "Ciclo longo",
    icon: "compass",
    accent: "var(--color-accent)",
  },
  {
    id: "lojistas",
    name: "Lojistas",
    short: "Lojistas",
    subtitle: "Venda transacional",
    description: "Volume e agilidade comercial",
    cycle: "Ciclo curto",
    icon: "rocket",
    accent: "var(--color-corten)",
  },
];

// Stages por funil — sem "Contato" (removida) e sem "Perdido" no fluxo horizontal.
// Perda é status arquivado (badge no card / filtro à parte).
const STAGES_PROJETOS = [
  { id: "ENTRADA",     name: "Nova Entrada", short: "Entrada",  slaEmDias: 2  },
  { id: "OUT_NOVA",    name: "Novos",        short: "Novos",    slaEmDias: 5  },
  { id: "QUALIFICADO", name: "Qualificado",  short: "Qualif.",  slaEmDias: 7  },
  { id: "REUNIAO",     name: "Reunião",      short: "Reunião",  slaEmDias: 7  },
  { id: "PROPOSTA",    name: "Proposta",     short: "Proposta", slaEmDias: 10 },
  { id: "NEGOCIACAO",  name: "Negociação",   short: "Negoc.",   slaEmDias: 7  },
  { id: "GANHO",       name: "Ganho",        short: "Ganho"                   },
];
const STAGES_LOJISTAS = [
  { id: "ENTRADA",     name: "Nova Entrada", short: "Entrada", slaEmDias: 1 },
  // 2 dias nas tres: ciclo curto, e sem prazo o alerta de parado nao atua.
  // O prototipo omitia o campo aqui; era omissao, nao decisao (ver 0030).
  { id: "OUT_NOVA",    name: "Novos", short: "Novos", slaEmDias: 2 },
  { id: "QUALIFICADO", name: "Qualificado", short: "Qualif.", slaEmDias: 2 },
  { id: "ORCAMENTO",   name: "Orçamento", short: "Orçam.", slaEmDias: 2 },
  { id: "GANHO",       name: "Ganho", short: "Ganho" },
];
// Resolver geral
const STAGES_BY_FUNNEL = { projetos: STAGES_PROJETOS, lojistas: STAGES_LOJISTAS };
// Compat: união para nomes e drawer / timeline
const STAGES = [
  { id: "ENTRADA",     name: "Nova Entrada", short: "Entrada" },
  { id: "OUT_NOVA",    name: "Novos",     short: "Novos" },
  { id: "QUALIFICADO", name: "Qualificado", short: "Qualif." },
  { id: "REUNIAO",     name: "Reunião",   short: "Reunião" },
  { id: "PROPOSTA",    name: "Proposta",  short: "Proposta" },
  { id: "NEGOCIACAO",  name: "Negociação", short: "Negoc." },
  { id: "ORCAMENTO",   name: "Orçamento", short: "Orçam." },
  { id: "PEDIDO",      name: "Pedido",    short: "Pedido" },
  { id: "FATURAMENTO", name: "Faturamento", short: "Fatur." },
  { id: "PRODUCAO",    name: "Produção",    short: "Produção" },
  { id: "GANHO",       name: "Ganho",     short: "Ganho" },
  { id: "PERDIDO",     name: "Perdido",   short: "Perdido" },
];
const STAGE_NAMES = Object.fromEntries(STAGES.map(s => [s.id, s.name]));

const TIPO_CLIENTE = [
  { id: "projeto_canal",  label: "Projeto · Canal",  short: "P · Canal",  funil: "projetos", color: "var(--color-accent)",   tone: "var(--color-accent-soft)" },
  { id: "projeto_direto", label: "Projeto · Direto", short: "P · Direto", funil: "projetos", color: "var(--color-cool)",     tone: "var(--color-cool-soft)" },
  { id: "lojista",        label: "Lojista",          short: "Lojista",    funil: "lojistas", color: "var(--color-corten)",   tone: "var(--color-corten-soft)" },
];
const TIPO_CLIENTE_BY_ID = Object.fromEntries(TIPO_CLIENTE.map(t => [t.id, t]));

// ----- Agentes / Usuários -----
// Canais de operação de um agente (espelha agentes_ia.canal na migration 0018).
const AGENT_CHANNELS = ["WhatsApp", "E-mail", "Site / formulário", "Telefone", "LinkedIn", "ERP / integração", "Outro"];
const AGENT_COLORS = ["#0E7C86", "#4A6BC3", "#9C5A3C", "#2E7D5B", "#C9821E"];
// Cadastro vivo: Admin → Agentes & tokens → Agentes mantém esta lista.
const AGENTS = [
  { id: "ag_wpp",       name: "Agente WhatsApp v2", color: "#0E7C86", canal: "WhatsApp",         descricao: "Recebe e triagem de conversas do WhatsApp comercial.", ativo: true },
  { id: "ag_helios",    name: "Helios Prospecção",  color: "#4A6BC3", canal: "Site / formulário", descricao: "Prospecção ativa e enriquecimento de dados de empresa.", ativo: true },
  { id: "ag_qualifier", name: "Qualificador LLM",   color: "#9C5A3C", canal: "Outro",            descricao: "Lê a transcrição da conversa e grava score e BANT.", ativo: true },
];

// Equipe: adicionada em produção via Admin → Convidar usuário (sem seeds de demonstração).
// Conta funcional do chão de fábrica: um login por setor, sem dado comercial.
// Vazia desde 21/08: os usuários reais vêm do banco (tabela `profiles`), carregados pelo
// store. A única entrada aqui era o "Produção PDLED", fictício — e como nenhum registro real
// aponta para o id dele, as buscas espalhadas pelas telas já falhavam para gente de verdade.
// Migrado em 24/08 (dívida N2): as 12 telas que liam esta constante agora leem `store.users`
// (a lista viva vinda de `profiles`) — Admin e Gestão não podiam discordar sobre quem é a
// equipe. NÃO apagar a constante: ela ainda sustenta a geração de dados de exemplo abaixo
// (`pick(USERS)`, `_SDRS`, etc.) — esse uso é interno a este módulo, que não pode importar o
// store, e opera sobre listas já vazias por conta própria, então não corrompe nada ao ficar.
const USERS = [];

// ----- Helpers -----
// Reais SEM centavos. Existe para dashboard, card de kanban e KPI, onde o
// centavo e ruido e a comparacao entre numeros e o que importa.
function brl(n) {
  if (!n) return "R$ 0";
  return "R$ " + n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
}

// Reais COM centavos. Use em todo lugar onde o numero e um COMPROMISSO e nao um
// indicador: linha de proposta, total, parcela, e o PDF que vai ao cliente.
//
// POR QUE OS DOIS EXISTEM (achado do Jeferson, 14/09). Ele digitou 152,59 no
// preco da fonte e a tela mostrou "R$ 153". O dado estava certo -- `dinheiro()`
// guardou 152.59 --; quem arredondava era o FORMATADOR, com
// `maximumFractionDigits: 0`. Isso e aceitavel num KPI e inaceitavel numa
// proposta: o `brl` antigo formatava tambem o PDF do cliente (proposta.jsx:816,
// 819, 934), entao o cliente recebia preco unitario e total em reais inteiros,
// somava na calculadora dele e dava diferente do nosso.
//
// `minimumFractionDigits` junto com o maximo: sem o minimo, 150 sairia "R$ 150"
// e 152,59 sairia "R$ 152,59" na mesma coluna -- numeros de tamanhos diferentes
// numa tabela de dinheiro.
// A DEFINICAO mora em supabase/functions/_shared/proposta-documento.js, junto do
// documento que a usa -- e importada la em cima para continuar disponivel pelos
// mesmos imports de sempre. Duas copias do formatador acabariam divergindo, e
// foi divergencia de formatacao que imprimiu "R$ 153" no lugar de "R$ 152,59".
function brlCompact(n) {
  if (!n) return "R$ 0";
  if (n >= 1_000_000) return "R$ " + (n / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 }) + "M";
  // Uma casa: 1.500 era "R$ 2k" -- a meta de 1.500 aparecia como 2 mil (Kaio, 25/09).
  if (n >= 1_000) return "R$ " + (n / 1_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 }) + "k";
  // Ponto flutuante: 100,10 + 200,20 mostrava "R$ 300.29999999999995". Inteiro continua sem casas ("R$ 999").
  const casas = Number.isInteger(n) ? 0 : 2;
  return "R$ " + n.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

let _seed = 7;
function rand() { _seed = (_seed * 9301 + 49297) % 233280; return _seed / 233280; }
function pick(arr) { return arr[Math.floor(rand() * arr.length)]; }

// ===== Nichos =====
const NICHOS = [
  { id: "resid_alto",  label: "Residência alto padrão",   icon: "compass",     palette: ["Beam Pro", "Spot LED CRI 95", "Driver DALI"], focus: "luz quente, cenas, dimerização" },
  { id: "hospitalidade", label: "Hospitalidade",           icon: "sparkles",    palette: ["Painel acústico", "Pendente Decorativo", "Beam Pro"], focus: "áreas comuns + cenas DALI" },
  { id: "foodservice",  label: "Foodservice / restaurantes", icon: "flame",       palette: ["Trilho eletrificado", "Pendente Decorativo", "Spot Direcionável"], focus: "luz quente, alto IRC" },
  { id: "corporativo",  label: "Arquitetura corporativa",   icon: "layout-kanban", palette: ["Beam Pro", "Painel acústico", "Sensor Presença"], focus: "economia + conforto visual" },
  { id: "varejo",       label: "Varejo / loja física",      icon: "tag",         palette: ["Trilho eletrificado", "Spot Direcionável", "Driver DALI"], focus: "alto contraste, modular" },
  { id: "industrial",   label: "Industrial",               icon: "settings",    palette: ["Luminária IP65", "Sensor Presença", "Refletor LED"], focus: "robustez, IK10/IP65" },
  { id: "educacional",  label: "Educacional",              icon: "file-text",   palette: ["Beam Pro", "Painel acústico", "Driver DALI"], focus: "baixo ofúscamento, manutenção fácil" },
];
const NICHOS_BY_ID = Object.fromEntries(NICHOS.map(n => [n.id, n]));

// ===== Produtos (catálogo) =====
// status: ativo | sob_encomenda | descontinuado
// giro: alto | medio | baixo | encalhado
// idUnidadeMedida: 2 = Metro, 4 = Unidade (Nomus)
// Catálogo oficial: importado via Produtos → "Incluir lista" ou sync Nomus (sem seeds de demonstração).
const PRODUTOS = [];
const PRODUTOS_BY_ID = Object.fromEntries(PRODUTOS.map(p => [p.id, p]));

function recomendacaoParaCliente(cliente) {
  if (!cliente?.nicho) return [];
  const consumiu = new Set((cliente.curvaConsumo || []).map(c => c.produtoId));
  // Filter products for this niche, exclude what they already buy, sort by giro
  const giroRank = { alto: 3, medio: 2, baixo: 1, encalhado: 0 };
  return PRODUTOS
    .filter(p => p.nichos.includes(cliente.nicho) && !consumiu.has(p.id) && p.giro !== "encalhado")
    .sort((a, b) => giroRank[b.giro] - giroRank[a.giro])
    .slice(0, 4);
}

// ===== Validação / máscaras fiscais =====
function maskCNPJ(value) {
  const v = String(value || "").replace(/\D/g, "").slice(0, 14);
  return v.replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");
}
function maskCPF(value) {
  const v = String(value || "").replace(/\D/g, "").slice(0, 11);
  return v.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function maskCEP(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");
}
function validateCNPJ(cnpj) {
  const c = String(cnpj || "").replace(/\D/g, "");
  if (c.length !== 14) return false;
  if (/^(\d)\1+$/.test(c)) return false;
  const calc = (slice) => {
    let weights = slice.length === 12 ? [5,4,3,2,9,8,7,6,5,4,3,2] : [6,5,4,3,2,9,8,7,6,5,4,3,2];
    let sum = 0;
    for (let i = 0; i < slice.length; i++) sum += Number(slice[i]) * weights[i];
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = calc(c.slice(0, 12));
  const d2 = calc(c.slice(0, 12) + d1);
  return d1 === Number(c[12]) && d2 === Number(c[13]);
}
function validateCPF(cpf) {
  const c = String(cpf || "").replace(/\D/g, "");
  if (c.length !== 11) return false;
  if (/^(\d)\1+$/.test(c)) return false;
  let s1 = 0, s2 = 0;
  for (let i = 0; i < 9; i++) s1 += Number(c[i]) * (10 - i);
  let d1 = (s1 * 10) % 11; if (d1 === 10) d1 = 0;
  for (let i = 0; i < 10; i++) s2 += Number(c[i]) * (11 - i);
  let d2 = (s2 * 10) % 11; if (d2 === 10) d2 = 0;
  return d1 === Number(c[9]) && d2 === Number(c[10]);
}


// ===== Anexos (arquivos por negócio) =====
// Tipos suportados: pdf, dwg, dxf, xlsx, csv, png, jpeg
// (Cada anexo é um mock seed; em produção o sistema armazena os binários.)
const MIME_BY_EXT = {
  pdf:  { label: "PDF",      color: "#C0453B", icon: "file-text",       preview: true  },
  dwg:  { label: "CAD DWG",  color: "#3F5D9A", icon: "file-spreadsheet", preview: false },
  dxf:  { label: "CAD DXF",  color: "#4A6BC3", icon: "file-spreadsheet", preview: false },
  xlsx: { label: "Excel",    color: "#1F8A5B", icon: "file-spreadsheet", preview: false },
  csv:  { label: "CSV",      color: "#2E7D5B", icon: "file-spreadsheet", preview: false },
  png:  { label: "PNG",      color: "#6750a4", icon: "image",            preview: true  },
  jpeg: { label: "JPEG",     color: "#9C5A3C", icon: "image",            preview: true  },
  jpg:  { label: "JPEG",     color: "#9C5A3C", icon: "image",            preview: true  },
};
const MAX_ATTACHMENT_SIZE_MB = 50;

const ANEXOS = {};

// ===== Canais de venda / Especificadores =====
// Migrados para o modelo unificado: um canal É um cliente com isCanal:true.
// Ver os seeds esp_* dentro de CLIENTES_SEEDS abaixo.

// ===== Clientes =====
// Status derivado automaticamente: ativo (compra <180d), inativo (sem compra há 180+ dias), prospec (nunca comprou).
// O campo `status` dos seeds é apenas fallback para canais (isCanal) — clientes comuns recalculam pela última compra.
const DIAS_INATIVIDADE = 180;
function parseLastBuyDias(lastBuy) {
  if (!lastBuy || lastBuy === "—") return null;
  const m = String(lastBuy).match(/(\d+)\s*(dia|mês|mes)/i);
  if (!m) return null;
  const n = Number(m[1]);
  return /dia/i.test(m[2]) ? n : n * 30;
}
function statusCarteira(lastBuyDias, faturamento) {
  if (lastBuyDias == null) return faturamento > 0 ? "ativo" : "prospec";
  return lastBuyDias > DIAS_INATIVIDADE ? "inativo" : "ativo";
}
const CLIENTES_SEEDS = [];

// Generate fiscal + address + curva de consumo deterministically per cliente
const CIDADES_ESTADOS = { "São Paulo": "SP", "Búzios": "RJ", "Porto Alegre": "RS", "Florianópolis": "SC", "Joinville": "SC", "Gramado": "RS", "Belo Horizonte": "MG", "Curitiba": "PR", "Recife": "PE", "Goiânia": "GO" };

function buildCurvaConsumo(cliente, idx) {
  if (cliente.status === "prospec") return [];
  const nicho = NICHOS_BY_ID[cliente.nicho];
  const candidates = PRODUTOS.filter(p => p.nichos.includes(cliente.nicho) && p.giro !== "encalhado");
  const consumed = candidates.slice(0, 3 + (idx % 3));
  const fat = cliente.faturamento || 100000;
  const total = consumed.reduce((s, p) => s + p.precoLista, 0);
  return consumed.map((p, i) => {
    const share = (p.precoLista / total) * fat;
    const qtd = Math.max(2, Math.round(share / p.precoLista));
    return {
      produtoId: p.id,
      nome: p.nome,
      categoria: p.categoria,
      qtdTotal: qtd,
      valorTotal: qtd * p.precoLista,
      ultimaCompra: ["há 7 dias", "há 14 dias", "há 28 dias", "há 45 dias"][i % 4],
    };
  }).sort((a, b) => b.valorTotal - a.valorTotal);
}

// Gera documentos fiscais VÁLIDOS (dígitos verificadores corretos) de forma determinística,
// para que o cadastro fiscal dos clientes-seed passe na validação e libere a geração de propostas.
function cnpjCheckDigits(base12) {
  const calc = (slice) => {
    const weights = slice.length === 12 ? [5,4,3,2,9,8,7,6,5,4,3,2] : [6,5,4,3,2,9,8,7,6,5,4,3,2];
    let sum = 0;
    for (let k = 0; k < slice.length; k++) sum += Number(slice[k]) * weights[k];
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = calc(base12);
  const d2 = calc(base12 + d1);
  return base12 + d1 + d2;
}
function cpfCheckDigits(base9) {
  let s1 = 0, s2 = 0;
  for (let k = 0; k < 9; k++) s1 += Number(base9[k]) * (10 - k);
  let d1 = (s1 * 10) % 11; if (d1 === 10) d1 = 0;
  const base10 = base9 + d1;
  for (let k = 0; k < 10; k++) s2 += Number(base10[k]) * (11 - k);
  let d2 = (s2 * 10) % 11; if (d2 === 10) d2 = 0;
  return base10 + d2;
}
function genValidCNPJ(i) {
  // base raiz determinística (8 dígitos) + filial 0001
  let raiz = String(10200000 + i * 137911).slice(0, 8).padStart(8, "0");
  return maskCNPJ(cnpjCheckDigits(raiz + "0001"));
}
function genValidCPF(i) {
  let base = String(100000000 + i * 7654321).slice(0, 9).padStart(9, "0");
  return maskCPF(cpfCheckDigits(base));
}
// Pessoa física para imóveis residenciais; pessoa jurídica para o restante.
function inferTipoDoc(s) {
  if (s.tipoDoc) return s.tipoDoc;
  if (s.isCanal) return "cnpj";
  return /^(Resid[êe]ncia|Casa|Loft|Cobertura|Apartamento|Studio Residencial)/i.test(s.nome) ? "cpf" : "cnpj";
}

const CLIENTES = CLIENTES_SEEDS.map((s, i) => {
  const cidadeBase = s.cidade.split(" · ")[0];
  const uf = s.cidade.split(" · ")[1] || "SP";
  const tipoDoc = inferTipoDoc(s);
  const c = {
    id: s.id || ("cli_" + (1000 + i)),
    ...s,
    tipoDoc,
    cnpj: tipoDoc === "cnpj" ? genValidCNPJ(i + 3) : "",
    cpf:  tipoDoc === "cpf"  ? genValidCPF(i + 5) : "",
    ie: tipoDoc === "cnpj" ? (110000000 + i * 1234).toString().slice(0, 9) : "isento",
    endereco: {
      cep: maskCEP("01" + (310 + i * 13).toString().padStart(3, "0") + (i * 10 + 100).toString().padStart(3, "0")),
      rua: pick(["Av. Paulista", "R. Augusta", "Av. Brig. Faria Lima", "R. Oscar Freire", "Av. Atlântica", "Av. Beira Mar"]),
      num: String(100 + i * 47),
      complemento: i % 3 === 0 ? `Sala ${1200 + i * 10}` : (i % 3 === 1 ? `Bloco B / Apto ${100 + i}` : ""),
      bairro: pick(["Jardins", "Itaim Bibi", "Vila Madalena", "Centro", "Pinheiros", "Higienópolis"]),
      cidade: cidadeBase,
      uf,
    },
    enderecoEntregaMesmo: i % 4 !== 0,
    enderecoEntrega: i % 4 === 0 ? {
      cep: maskCEP("04" + (210 + i * 7).toString().padStart(3, "0") + (i * 8 + 200).toString().padStart(3, "0")),
      rua: "R. da Obra",
      num: String(500 + i * 11),
      complemento: "Galpão",
      bairro: "Distrito Industrial",
      cidade: cidadeBase, uf,
    } : null,
    telefone: "+55 11 9" + (Math.floor(10000000 + i * 7777) + "").slice(0, 4) + "-" + (Math.floor(1000 + i * 137) + "").slice(0, 4),
    email: s.contato.toLowerCase().replace(/[^a-z]/g, ".") + "@" + s.nome.toLowerCase().replace(/[^a-z]/g, "").slice(0, 10) + ".com.br",
    cargo: s.isCanal ? "Responsável comercial" : ["Proprietário(a)", "Diretor(a) Comercial", "Comprador(a)", "Sócio(a)"][i % 4],
    contatoDecisor: !s.isCanal && i % 3 !== 1,
    ownerId: pick(USERS).id,
    criadoEm: pick(["Fev 2024", "Mai 2024", "Set 2024", "Jan 2025", "Mar 2025", "Jul 2025", "Out 2025", "Jan 2026"]),
  };
  // Status derivado pela regra de carteira (180 dias sem compra ⇒ inativo)
  c.lastBuyDias = parseLastBuyDias(s.lastBuy);
  c.status = s.isCanal ? s.status : statusCarteira(c.lastBuyDias, s.faturamento || 0);
  // Múltiplos contatos — o primeiro é o principal (mantém c.contato em sincronia)
  const dominio = c.email.split("@")[1];
  c.contatos = [
    { id: `ct_${i}a`, nome: s.contato, cargo: c.cargo, telefone: c.telefone, email: c.email, principal: true },
    ...(!s.isCanal && i % 3 === 0 ? [{
      id: `ct_${i}b`,
      nome: pick(["Camila Duarte", "Jorge Lins", "Beatriz Nóbrega", "Tiago Sampaio", "Vera Antunes"]),
      cargo: "Financeiro",
      telefone: "+55 11 9" + (4300 + i * 31) + "-" + (1100 + i * 53),
      email: "financeiro@" + dominio,
      principal: false,
    }] : []),
  ];
  // Dados para faturamento (NF-e / cobrança)
  c.emailFinanceiro = s.tipoDoc === "cnpj" ? "financeiro@" + dominio : c.email;
  c.condPagamento = pick(["À vista", "28 dias", "30/60", "30/60/90"]);
  c.curvaConsumo = buildCurvaConsumo(c, i);
  return c;
});

// Cadastro fiscal completo é obrigatório para gerar propostas
function fiscalOk(cliente) {
  if (!cliente) return false;
  return cliente.tipoDoc === "cnpj" ? validateCNPJ(cliente.cnpj) : validateCPF(cliente.cpf);
}

// ===== Emissoras das propostas (PDLED / PDLIGHT) =====
// Empresa emissora selecionável na geração da proposta. Os dados aparecem no cabeçalho do PDF A4.
// A definicao das emissoras mora em supabase/functions/_shared/emissoras.js,
// junto do documento que as imprime -- ver a nota la. Importadas no topo deste
// arquivo para continuarem disponiveis pelos mesmos imports de sempre.

// Aviso e condicoes comerciais padrao: moram em _shared, para o PDF do CRM e a
// pagina do link usarem o MESMO texto (o do exemplo aprovado pelo Kaio, 25/09).

// ===== Formas de pagamento (idFormaPagamento = Nomus · GET /formasPagamento) =====
const FORMAS_PAGAMENTO_LIST = [
  { id: 10, nome: "Boleto Bancário" },
  { id: 13, nome: "Transferência bancária / PIX" },
  { id: 3,  nome: "Cartão de Crédito" },
  { id: 4,  nome: "Cartão de Débito" },
  { id: 5,  nome: "Crédito Loja" },
  { id: 2,  nome: "Cheque" },
  { id: 12, nome: "Outros" },
  { id: 11, nome: "Sem pagamento" },
];
const FORMAS_PAGAMENTO_BY_ID = Object.fromEntries(FORMAS_PAGAMENTO_LIST.map(f => [f.id, f]));
const FORMAS_PAGAMENTO = FORMAS_PAGAMENTO_LIST.map(f => f.nome); // compat: exibição

// ===== Modalidade de transporte (modalidadeTransporte = código NF-e) =====
const TRANSPORTE_MODALIDADES = [
  { id: 0, nome: "CIF · por conta do emitente" },
  { id: 1, nome: "FOB · por conta do destinatário" },
  { id: 2, nome: "Por conta de terceiros" },
  { id: 3, nome: "Transporte próprio · emitente" },
  { id: 4, nome: "Transporte próprio · destinatário" },
  { id: 9, nome: "Sem transporte" },
];
const TRANSPORTE_BY_ID = Object.fromEntries(TRANSPORTE_MODALIDADES.map(t => [t.id, t]));
const TRANSPORTADORAS = TRANSPORTE_MODALIDADES.map(t => t.nome); // compat: exibição

// ===== Transporte — listas espelhadas do Nomus =====
// Cadastro de transportadoras do Nomus (id = Código da pessoa → idPessoaTransportadora).
// Origem: uploads/TRANSPORTADORAS.csv
const TRANSPORTADORAS_CADASTRO = [
  { id: 264, nome: "A Nova Era do Transportes", cnpj: "19.858.459/0002-30" },
  { id: 233, nome: "ACCERT TRANSPORTES E LOGÍSTICA", cnpj: "05.222.092/0007-63" },
  { id: 844, nome: "ALFA TRANSPORTES EIRELI", cnpj: "82.110.818/0003-93" },
  { id: 1162, nome: "ALURI TRANSPORTES LTDA", cnpj: "08.310.412/0001-30" },
  { id: 424, nome: "BINHO TRANSPORTES E LOGISTICA LTDA", cnpj: "05.112.286/0001-10" },
  { id: 825, nome: "BRASPRESS TRANSPORTES URGENTES LTDA", cnpj: "48.740.351/0088-16" },
  { id: 845, nome: "BRASPRESS TRANSPS URGENTES LTDA", cnpj: "48.740.351/0004-08" },
  { id: 482, nome: "CAMILO DOS SANTOS FILHO LTDA", cnpj: "19.451.038/0003-70" },
  { id: 398, nome: "CARVALIMA TRANSPORTES  LTDA", cnpj: "33.070.814/0010-42" },
  { id: 1249, nome: "CCA EXPRESS", cnpj: "02.463.394/0003-01" },
  { id: 832, nome: "CORREIOS  - RN", cnpj: "34.028.316/3188-98" },
  { id: 341, nome: "CORREIOS - MG", cnpj: "06.241.085/0001-86" },
  { id: 301, nome: "CORREIOS - RIO", cnpj: "02.620.463/0001-81" },
  { id: 732, nome: "DIRECIONAL TRANSP. E LOGISTICA S/A", cnpj: "08.219.203/0006-90" },
  { id: 828, nome: "ELTON J V DA SILVA TRANSPORTES", cnpj: "35.944.824/0001-02" },
  { id: 1050, nome: "EMPRESA DE TRANSPORTES PAJUCARA LTDA", cnpj: "53.237.962/0031-40" },
  { id: 457, nome: "ENTRE PONTOS", cnpj: "04.142.031/0001-38" },
  { id: 1353, nome: "EXPRESSO M2000 LTDA", cnpj: "26.341.222/0002-42" },
  { id: 543, nome: "FEDEX BRASIL LOG.E TRANSPORTE LTDA", cnpj: "10.970.887/0008-70" },
  { id: 1090, nome: "FIEZA CARGAS LTDA", cnpj: "42.373.347/0001-20" },
  { id: 461, nome: "GLOBAL AIR CARGO LT", cnpj: "00.945.958/0001-55" },
  { id: 500, nome: "GOL LINHAS AEREAS S.A.", cnpj: "07.575.651/0001-59" },
  { id: 1424, nome: "HJT TRANSPORTES & LOGISTICA LTDA", cnpj: "07.130.717/0007-92" },
  { id: 693, nome: "JADLOG LOGISTICA S.A", cnpj: "04.884.082/0015-30" },
  { id: 463, nome: "JAMEF ENCOMENDAS URGENTES", cnpj: "20.147.617/0011-13" },
  { id: 206, nome: "JAMEF TRANSP LTDA", cnpj: "20.147.617/0022-76" },
  { id: 337, nome: "Jamef Transportes Limitada", cnpj: "20.147.617/0023-57" },
  { id: 340, nome: "JAMEF TRANSPORTES LTDA", cnpj: "20.147.617/0020-04" },
  { id: 339, nome: "JAMEF TRANSPORTES LTDA.", cnpj: "20.147.617/0026-08" },
  { id: 1421, nome: "JOAQUIM MARIO VIEIRA TRANSPORTES", cnpj: "06.237.779/0001-40" },
  { id: 560, nome: "KR TRANSPORTES E LOGÍSTICA LTDA - SP", cnpj: "05.597.965/0001-27" },
  { id: 1148, nome: "LZP AGENCIAMENTO DE CARGAS LTDA", cnpj: "38.049.053/0001-70" },
  { id: 1091, nome: "M W TRANSPORTES LTDA", cnpj: "17.676.693/0001-30" },
  { id: 512, nome: "MONTENEGRO TRANSPORTES DE CARGAS", cnpj: "22.188.831/0002-52" },
  { id: 1196, nome: "MOVVI LOGISTICA LTDA", cnpj: "23.864.838/0001-29" },
  { id: 1300, nome: "NOMAR TRANSPORTE E LOCACAO LTDA ME", cnpj: "03.820.588/0001-18" },
  { id: 38, nome: "ONCA TRANSPORTES & LOGISTICA LTDA.", cnpj: "07.333.662/0001-22" },
  { id: 338, nome: "PROPRIA", cnpj: "000.000.001-91" },
  { id: 356, nome: "RODONAVES TRANSPORTES E ENCOMENDAS LTDA", cnpj: "44.914.992/0013-71" },
  { id: 36, nome: "RODOVITOR TRANSPORTES", cnpj: "08.408.736/0002-96" },
  { id: 1201, nome: "SEDEX - EMPRESA BRASILEIRA DE CORREIOS E TELÉGRAFO", cnpj: "34.028.316/0001-03" },
  { id: 1033, nome: "SETE LAGOAS TRANSPORTES LTDA", cnpj: "03.098.929/0004-36" },
  { id: 706, nome: "STM TRANSPORTES RODOVIARIOS LTDA", cnpj: "04.271.846/0001-17" },
  { id: 1048, nome: "SUPER URGENTE TRANSPORTE AEREO E RODOVIARIO LTDA", cnpj: "13.642.117/0001-10" },
  { id: 67, nome: "TIME LOGISTICA", cnpj: "30.371.404/0001-52" },
  { id: 1398, nome: "TRANS LTR", cnpj: "13.452.705/0001-91" },
  { id: 720, nome: "TRANSCAMPELO CARGAS RODOVIÁRIAS LTDA", cnpj: "03.691.363/0001-09" },
  { id: 855, nome: "TRANSCARAPIA TRANSPORTES LTDA", cnpj: "00.904.848/0001-45" },
  { id: 39, nome: "Transportadora Actual Cargo LTDA", cnpj: "04.961.504/0002-00" },
  { id: 33, nome: "TRANSPORTADORA GAMPER LTDA", cnpj: "77.778.892/0006-95" },
  { id: 37, nome: "Transportadora Gran Cargo", cnpj: "05.915.569/0001-09" },
  { id: 1199, nome: "TRANSPORTADORA MINUANO LTDA", cnpj: "87.183.570/0021-96" },
  { id: 1087, nome: "TRANSPORTADORA PKLOG BRASIL EXPRESS", cnpj: "47.407.603/0001-76" },
  { id: 107, nome: "TRANSPORTADORA TIME LOGISTICA EIRELI", cnpj: "19.630.180/0001-13" },
  { id: 954, nome: "TRANSPORTES SERRANA", cnpj: "11.389.565/0002-00" },
  { id: 1190, nome: "VIPEX TRANSPORTES LTDA", cnpj: "04.188.086/0002-60" },
];
const TRANSPORTADORA_BY_ID = Object.fromEntries(TRANSPORTADORAS_CADASTRO.map(t => [t.id, t]));
const SERVICOS_ENTREGA = ["Entrega padrão", "Entrega agendada", "Retirada no CD", "Expresso"];
const TIPOS_REDESPACHO = ["Sem redespacho", "Redespacho por conta do remetente", "Redespacho por conta do destinatário", "Redespacho por conta de terceiros"];
const INTEGRACOES_LOGISTICA = ["Nenhuma"];

// Método para rateio de frete/seguro/outras despesas entre os itens (padrão: 2)
const METODOS_RATEIO = [
  { id: 0, nome: "0) Não ratear", hint: "valores ficam só no total do pedido" },
  { id: 1, nome: "1) Ratear igualmente entre os itens", hint: "divide pelo nº de itens" },
  { id: 2, nome: "2) Ratear de acordo com o valor bruto total de cada item", hint: "proporcional ao valor do item" },
];

// ===== UFs =====
const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

// ===== Datas: prazos comerciais =====
const isoDia = (d) => { const p = n => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
function addDiasCorridos(base, dias) { const d = new Date(base); d.setDate(d.getDate() + Number(dias || 0)); return d; }
function addDiasUteis(base, dias) {
  const d = new Date(base); let restam = Number(dias || 0);
  while (restam > 0) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) restam--; }
  return d;
}
// Data resultante de um prazo em dias (úteis ou corridos), em ISO yyyy-mm-dd
function dataDoPrazo(dias, uteis, base) {
  const b = base ? new Date(String(base).length <= 10 ? base + "T00:00:00" : base) : new Date();
  return isoDia(uteis ? addDiasUteis(b, dias) : addDiasCorridos(b, dias));
}
const dataBR = (iso) => iso ? new Date(String(iso).length <= 10 ? iso + "T00:00:00" : iso).toLocaleDateString("pt-BR") : "—";

// ===== Helpers de formato Nomus =====
// O Nomus espera datas "DD/MM/YYYY HH:MM:SS" e números como string BR ("91,12").
function nomusDataHora(v) {
  const dt = v instanceof Date ? v : (v ? new Date(String(v).length <= 10 ? v + "T00:00:00" : v) : null);
  if (!dt || isNaN(dt)) return null;
  const p = n => String(n).padStart(2, "0");
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}:${p(dt.getSeconds())}`;
}
function nomusData(v) { const s = nomusDataHora(v); return s ? s.slice(0, 10) : null; }
// Quantidade/valor: string com vírgula decimal e 2 casas (qtd aceita até 4).
// `toFixed` sozinho NÃO serve para dinheiro: a MDN documenta que ele erra o
// arredondamento quando o valor não tem representação exata em float
// ("(2.55).toFixed(1) // '2.5' — it rounds down…"). Aqui isso viraria pedido de
// compra com centavo diferente do que o cliente aprovou. Por isso o valor passa
// por `dinheiro()` ANTES; o toFixed depois só formata as casas, já sobre um
// número que é exatamente centavo.
// Quando `casas` > 2 o valor não é dinheiro (quantidade aceita 4) — não arredonda.
function nomusNum(n, casas = 2) {
  const v = casas === 2 ? dinheiro(n) : Number(n || 0);
  return v.toFixed(casas).replace(".", ",");
}
// Valores de frete/seguro no raiz vão como string com PONTO decimal ("180.00").
function nomusValor(n) { return dinheiro(n).toFixed(2); }

// ===== Famílias e Grupos (hierarquia do catálogo Nomus) =====
const FAMILIAS = [
  { id: "fam_perfis",  nome: "Perfis Lineares",    grupos: ["Perfil de Sobrepor", "Perfil de Embutir", "Perfil Pendente", "Perfil de Piso", "Perfil Rodapé", "Perfil Externo"] },
  { id: "fam_spots",   nome: "Spots e Decos",       grupos: ["Spot", "Deco", "Balizador", "Mini Spot"] },
  { id: "fam_fitas",   nome: "Fitas e Neon",        grupos: ["Fita de Led", "Neon", "Elastic", "Skyline", "Magic Line"] },
  { id: "fam_fontes",  nome: "Fontes e Acessórios", grupos: ["Fontes", "Acessórios", "Controle", "End Cap", "Canopla", "Clip"] },
];
const FAMILIAS_BY_ID = Object.fromEntries(FAMILIAS.map(f => [f.id, f]));
const GRUPO_TO_FAMILIA = {};
FAMILIAS.forEach(f => f.grupos.forEach(g => { GRUPO_TO_FAMILIA[g] = f.id; }));

// ===== Tipo de Venda (Nomus idTipoMovimentacao) — sugestão por segmento =====
const TIPO_VENDA_OPCOES = [
  { id: 35, nome: "VENDA LOJISTA" },
  { id: 48, nome: "VENDA CORPORATIVA" },
  { id: 60, nome: "VENDAS EXTERIOR" },
];
function tipoVendaSugerido(tipoCliente) {
  if (tipoCliente === "lojista") return TIPO_VENDA_OPCOES.find(t => t.id === 35);
  if (tipoCliente === "exportacao") return TIPO_VENDA_OPCOES.find(t => t.id === 60);
  return TIPO_VENDA_OPCOES.find(t => t.id === 48);
}

// ===== Condições de Pagamento (pré-configuradas no CRM, idCondicaoPagamento = Nomus) =====
const CONDICOES_PAGAMENTO_LIST = [
  { id: 1,  nome: "À Vista" },
  { id: 2,  nome: "28 dias" },
  { id: 3,  nome: "30/60" },
  { id: 4,  nome: "30/60/90" },
  { id: 5,  nome: "Entrada + 30 dias" },
  { id: 6,  nome: "Entrada + 30/60" },
  { id: 7,  nome: "Entrada + 30/60/90" },
  { id: 8,  nome: "45/90" },
  { id: 9,  nome: "60/90/120" },
  { id: 10, nome: "A combinar" },
];

// ===== Integração ERP — Nomus EC (mock, pronto para ligar) =====
// Base: https://pdled.nomus.com.br/pdled/rest · POST /clientes · POST /pedidos
// Mantido aberto para integração futura: a proposta aprovada vira pedido no ERP.
const ERP_CONFIG = {
  nome: "Nomus EC",
  baseUrl: "https://pdled.nomus.com.br/pdled/rest",
  usuario: "integradorerp",
  conectado: true,
};
// tipoContribuinteICMS: 1 contribuinte, 2 isento, 3 não contribuinte
// A regra mora em src/cliente-erp.js (o valor gravado manda; a inferência pela
// IE só cobre o que falta). Esta cópia divergia -- e é a da prévia do payload.
const erpTipoContribuinte = tipoContribuinte;
// Monta o payload Nomus POST /clientes a partir do cadastro do CRM (preview de integração).
function buildErpClientePayload(cliente) {
  if (!cliente) return {};
  const end = cliente.endereco || {};
  const isPJ = cliente.tipoDoc === "cnpj";
  return {
    nome: cliente.nome,
    razaoSocial: cliente.razaoSocial || cliente.nome,
    tipoPessoa: isPJ ? 1 : 2,
    tipoContribuinteICMS: erpTipoContribuinte(cliente),
    ...(isPJ ? { cnpj: cliente.cnpj } : { cpf: cliente.cpf }),
    inscricaoEstadual: isPJ ? (cliente.ie === "isento" ? "" : (cliente.ie || "")) : "",
    ativo: true,
    email: cliente.email || "",
    telefone: cliente.telefone || "",
    endereco: end.rua || "",
    numero: end.num || "",
    complemento: end.complemento || "",
    bairro: end.bairro || "",
    municipio: end.cidade || "",
    uf: end.uf || "",
    cep: end.cep || "",
    pais: "BRASIL",
    suspenderCobranca: false,
  };
}

// ===== Leads / Negócios =====
// Each lead carries:
//  - funil ("projetos" | "lojistas")
//  - clienteId (cliente final — pagador/recebedor)
//  - especificadorId (opcional — canal que trouxe o projeto)
//  - tipoCliente (projeto_canal | projeto_direto | lojista)
//  - parceriaRepasse / pctRepasse / valorRepasse / statusRepasse
const LEAD_SEEDS = [];

// ----- Owner / originador & data de criação (para handoff SDR→Closer e filtros de período) -----
const _SDRS = USERS.filter(u => u.papel === "sdr" || u.papel === "bdr");
const _NOW_REF = new Date();
const _CREATED_BASE = new Date(_NOW_REF.getFullYear(), _NOW_REF.getMonth(), _NOW_REF.getDate());
function buildOriginador(s, i) {
  if (s.origem === "agente") {
    const ag = AGENTS.find(a => a.id === s.agenteId) || AGENTS[0];
    return { tipo: "ia", id: ag.id, nome: ag.name };
  }
  const sdr = _SDRS[i % _SDRS.length] || USERS[1];
  return { tipo: "sdr", id: sdr.id, nome: sdr.nome };
}
function buildCriadoData(s, i) {
  const d = new Date(_CREATED_BASE);
  d.setDate(d.getDate() - (i * 13 + (s.dias || 0)));
  return d;
}

// Compute valor repasse + status
function buildRepasse(seed) {
  if (seed.funil !== "projetos") return null;
  if (!seed.especificadorId || !seed.parceriaRepasse) return null;
  const base = seed.valor || 0;
  const valorRepasse = Math.round(base * (seed.pctRepasse / 100));
  if (seed.stage === "GANHO") return { valorRepasse, status: "Pendente",   when: "há 2 dias" };
  if (seed.stage === "PERDIDO") return null;
  return { valorRepasse, status: "Estimado", when: null };
}

const LEADS = LEAD_SEEDS.map((s, i) => ({
  id: "ld_" + (1000 + i),
  empresa: s.empresa,
  contato: s.contato,
  cargo: s.cargo,
  email: (s.contato || "").toLowerCase().replace(/[^a-z]/g, ".") + "@" + (s.empresa || "").toLowerCase().split(" ")[0].replace(/[^a-z]/g, "") + ".com.br",
  telefone: "+55 11 9" + (Math.floor(40000000 + i * 7777) + "").slice(0, 4) + "-" + (Math.floor(1000 + i * 137) + "").slice(0, 4),
  segmento: s.segmento,
  valor: s.valor,
  stage: s.stage,
  origem: s.origem,
  agenteId: s.agenteId || null,
  // Carteira determinística: distribui os negócios-seed entre usuários de exemplo,
  // para que os relatórios individuais tenham dados reais quando LEAD_SEEDS for populado.
  ownerId: s.ownerId || ["u_producao", "u_ana", "u_producao", "u_pedro", "u_producao", "u_rafa"][i % 6],
  calor: s.calor,
  diasNoEstagio: s.dias,
  proximaAcao: s.prox,
  proximaDue: s.proxDue,
  bantScore: s.bantScore,
  motivoPerda: s.motivoPerda || null,
  obsPerda: s.obsPerda || null,
  perdido: !!s.perdido,
  originador: buildOriginador(s, i),
  criadoData: buildCriadoData(s, i),
  tags: s.tags || [],
  externalId: s.origem === "agente" ? "ext_" + Math.floor(rand() * 1e9).toString(36) : null,
  criadoEm: s.origem === "agente" ? "via webhook" : "manual",
  // novos campos do spec
  funil: s.funil,
  clienteId: s.clienteId,
  especificadorId: s.especificadorId,
  tipoCliente: s.tipoCliente,
  parceriaRepasse: s.parceriaRepasse,
  pctRepasse: s.pctRepasse,
  repasse: buildRepasse(s),
  scoreFit: s.scoreFit || 0,
  fonteCadeia: s.fonteCadeia || "",
  pedidoNomus: s.pedidoNomus || null,
  previsaoEntrega: s.previsaoEntrega || null,
  ultimoMovimento: s.ultimoMovimentoDias != null
    ? new Date(_CREATED_BASE.getTime() - s.ultimoMovimentoDias * 86400000).toISOString()
    : null,
  cadeia: s.stage === "ENTRADA"
    ? [{ seq: 1, tipo: "geracao", atorTipo: "agente", atorId: s.agenteId || "ag_helios", atorNome: (AGENTS.find(a => a.id === (s.agenteId || "ag_helios")) || {}).name || "Agente Prospecção", papel: null, data: new Date(Date.now() - (s.dias || 0) * 86400000).toISOString(), meta: { scoreEnriquecimento: s.scoreFit || 0, fonte: s.fonteCadeia || "", segmento: s.segmento || "" } }]
    : [],
  bant: {
    budget: s.bantScore >= 2 ? (s.valor > 200000 ? "R$ 400k aprovado" : "R$ 80–150k previsto") : null,
    authority: s.bantScore >= 2 ? s.contato + " (decisor)" : null,
    need: s.bantScore >= 3 ? "Reduzir prazo de entrega · especificação técnica robusta" : (s.bantScore >= 1 ? "Mencionou ineficiência operacional" : null),
    timeline: s.bantScore >= 3 ? "Q3 2026" : (s.bantScore >= 1 ? "Sem prazo definido" : null),
  },
  dynamic: s.origem === "agente" ? {
    fonte: "AI_AGENT",
    fit_comercial: s.bantScore >= 3 ? "alto" : (s.bantScore >= 2 ? "médio" : "baixo"),
    urgencia: s.calor === "hot" ? "imediata" : (s.calor === "warm" ? "trimestre" : "longo_prazo"),
    canal_origem: pick(["WhatsApp", "LinkedIn", "Inbound site"]),
    score_llm: Math.round((40 + s.bantScore * 12 + (s.calor === "hot" ? 25 : s.calor === "warm" ? 12 : 0))),
  } : null,
}));

// ----- Lost reasons (específicos da PDLED) -----
const LOST_REASONS = [
  { id: "sem_budget",              label: "Sem verba / Budget insuficiente",         icon: "wallet"          },
  { id: "prazo_incompativel",      label: "Prazo incompatível com a obra",           icon: "clock"           },
  { id: "perdido_concorrente",     label: "Perdido para concorrente",                icon: "users"           },
  { id: "especificacao_diferente", label: "Produto especificado diferente do nosso", icon: "package"         },
  { id: "projeto_cancelado",       label: "Projeto/obra cancelado",                  icon: "x-circle"        },
  { id: "sem_resposta",            label: "Sem resposta do cliente",                 icon: "phone-off"       },
  { id: "fora_do_perfil",          label: "Fora do perfil PDLED",                    icon: "slash"           },
  { id: "preco",                   label: "Preço acima do mercado",                  icon: "trending-down"   },
  { id: "outro",                   label: "Outro motivo",                            icon: "more-horizontal" },
];

// ===== Alertas (com datas reais para a visão calendário) =====
// TODAY = data/hora reais do usuário — todo o mock é ancorado em offsets relativos a agora.
const TODAY = new Date();
function _d(addDays, h = 10, m = 0) {
  const d = new Date(TODAY);
  d.setDate(d.getDate() + addDays);
  d.setHours(h, m, 0, 0);
  return d;
}
const ALERT_SEEDS = [];
const ALERTS = ALERT_SEEDS.map(a => ({
  ...a,
  vencido: !a.feito && a.when < new Date(TODAY.getTime() - 60 * 60 * 1000),
  quando: formatAlertWhen(a.when),
}));

function formatAlertWhen(d) {
  const diffMs = d - TODAY;
  const sameDay = d.getFullYear() === TODAY.getFullYear() && d.getMonth() === TODAY.getMonth() && d.getDate() === TODAY.getDate();
  const tomorrow = new Date(TODAY); tomorrow.setDate(TODAY.getDate() + 1);
  const isTomorrow = d.getFullYear() === tomorrow.getFullYear() && d.getMonth() === tomorrow.getMonth() && d.getDate() === tomorrow.getDate();
  const yesterday = new Date(TODAY); yesterday.setDate(TODAY.getDate() - 1);
  const isYesterday = d.getFullYear() === yesterday.getFullYear() && d.getMonth() === yesterday.getMonth() && d.getDate() === yesterday.getDate();
  const pad = n => String(n).padStart(2, "0");
  const hhmm = pad(d.getHours()) + ":" + pad(d.getMinutes());
  if (sameDay) return "Hoje, " + hhmm;
  if (isTomorrow) return "Amanhã, " + hhmm;
  if (isYesterday) return "Ontem, " + hhmm;
  if (Math.abs(diffMs) < 7 * 24 * 60 * 60 * 1000) {
    const dia = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][d.getDay()];
    return dia + ", " + hhmm;
  }
  return pad(d.getDate()) + "/" + pad(d.getMonth() + 1) + " " + hhmm;
}

// ===== Gmail · threads sincronizadas (timeline) =====
const GMAIL_THREADS = {};

// A timeline FABRICADA foi removida em 18/09.
//
// `buildTimeline(lead)` montava sete tipos de evento a partir dos campos do
// negocio -- com autor fixo no codigo ("Ana Vargas"), data calculada
// (`diasNoEstagio + 5`) e texto inventado. A tela mostrava isso como historico.
//
// Os outros mocks deste projeto PERDEM dado: dizem "salvo" e nao salvam. Este
// INVENTAVA, e atribuia a acao a uma pessoa pelo nome. Numa timeline -- que e o
// que alguem consulta em passagem de bastao, cobranca interna ou disputa com
// cliente -- historico inventado e pior que historico ausente, porque ninguem
// tem motivo para desconfiar dele.
//
// O historico real agora vem de `timeline_eventos`, gravado por
// `appendTimeline` (src/store.jsx).

// ===== Webhooks =====
const WEBHOOKS = [];

// API logs
const API_LOGS = [];

// ===== Reports data =====
const COHORT = {
  rows: [],
  cols: ["S+0", "S+1", "S+2", "S+3", "S+4", "S+5", "S+6", "S+7"],
  data: [],
};

const FUNNEL_HEALTH = [];

const ATTRIBUTION = [
  { stage: "OUT_NOVA",    agent: 0, human: 0 },
  { stage: "QUALIFICADO", agent: 0, human: 0 },
  { stage: "REUNIAO",     agent: 0, human: 0 },
  { stage: "PROPOSTA",    agent: 0, human: 0 },
  { stage: "NEGOCIACAO",  agent: 0, human: 0 },
  { stage: "GANHO",       agent: 0, human: 0 },
];

const FUNNEL_COUNTS_PROJETOS = [
  { stage: "OUT_NOVA",    count: 0, pct: 0 },
  { stage: "QUALIFICADO", count: 0, pct: 0 },
  { stage: "REUNIAO",     count: 0, pct: 0 },
  { stage: "PROPOSTA",    count: 0, pct: 0 },
  { stage: "NEGOCIACAO",  count: 0, pct: 0 },
  { stage: "GANHO",       count: 0, pct: 0 },
];
const FUNNEL_COUNTS_LOJISTAS = [
  { stage: "OUT_NOVA",    count: 0, pct: 0 },
  { stage: "QUALIFICADO", count: 0, pct: 0 },
  { stage: "ORCAMENTO",   count: 0, pct: 0 },
  { stage: "GANHO",       count: 0, pct: 0 },
];
const FUNNEL_COUNTS = FUNNEL_COUNTS_PROJETOS; // compat

// Ranking de Arquitetos / Parceiros: agora derivado em runtime de store.especificadores
// (que vem dos clientes com isCanal). Ver store.jsx.

// Cards de insight do Dashboard. Aqui mora so a MOLDURA; valor e delta sao
// calculados no Dashboard a partir dos dados (paineis.js). Ate 23/09 a lista
// era vazia e a faixa de insights nunca aparecia -- embora o Dashboard ja
// calculasse de verdade os valores de i3 e i4.
const INSIGHTS = [
  { id: "i1", icon: "alert-triangle", tone: "warn", titulo: "Negócios em risco", hint: "Parados além da referência da etapa ou sem atividade — abre o Índice de Atrito" },
  { id: "i2", icon: "clock", tone: "warn", titulo: "Sem próxima ação", hint: "Negócios em aberto sem próximo passo combinado com o cliente" },
  { id: "i3", icon: "wallet", tone: "warn", scope: "team", titulo: "Repasses pendentes", hint: "Canais aguardando pagamento de repasse" },
  { id: "i4", icon: "history", tone: "danger", titulo: "Recorrentes inativos", hint: "Canais e lojistas sem compra recente" },
];

// ===== v4 · Meses para o filtro de relatórios =====
// Gerados dinamicamente: mês corrente + 5 anteriores (factor escala os dados mock).
const _MES_LABELS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const _MES_SHORT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const MONTHS = [1.00, 0.88, 0.79, 0.71, 0.64, 0.93].map((factor, i) => {
  const d = new Date(TODAY.getFullYear(), TODAY.getMonth() - i, 1);
  return {
    id: d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0"),
    label: _MES_LABELS[d.getMonth()] + " " + d.getFullYear(),
    short: _MES_SHORT[d.getMonth()] + "/" + String(d.getFullYear()).slice(2),
    factor,
  };
});
const MONTHS_BY_ID = Object.fromEntries(MONTHS.map(m => [m.id, m]));

// ===== v4 · Comissões por vendedor =====
// Cada vendedor só enxerga as suas; o gestor acompanha de todos (regra aplicada na UI).
const COMMISSION_RATES = { u_admin: 0.02 };

// Negócios-base (referência de Maio); escalonados por mês via factor.
const COMMISSION_DEALS = { u_admin: [] };

// Retorna o razão de comissões do mês para todos os usuários.
// scope: "self" (só o usuário) ou "team" (gestor vê todos).
// meAtual = usuário autenticado da sessão (store.me) — quem chama já está dentro
// do React tree e o repassa; esta função não tem acesso à sessão por conta própria.
function getCommissionLedger(monthId, ratesOverride, meAtual) {
  const m = MONTHS_BY_ID[monthId] || MONTHS[0];
  const f = m.factor;
  const isCurrent = m.id === MONTHS[0].id;
  const RATES = ratesOverride || COMMISSION_RATES;
  return [meAtual, ...USERS].filter(Boolean).map(u => {
    const rate = RATES[u.id] != null ? RATES[u.id] : (COMMISSION_RATES[u.id] || 0);
    const deals = (COMMISSION_DEALS[u.id] || []).filter(d => d.valor > 0).map(d => {
      const valor = Math.round((d.valor * f) / 1000) * 1000;
      const comissao = Math.round(valor * rate);
      // Meses passados já estão liquidados.
      const status = isCurrent ? d.status : "pago";
      return { ...d, valor, comissao, status, mes: m.short };
    });
    const receita = deals.reduce((s, d) => s + d.valor, 0);
    const comissaoTotal = deals.reduce((s, d) => s + d.comissao, 0);
    const pago = deals.filter(d => d.status === "pago").reduce((s, d) => s + d.comissao, 0);
    const pendente = comissaoTotal - pago;
    return {
      userId: u.id, nome: u.nome, initials: u.initials, papel: u.papel,
      rate, receita, comissaoTotal, pago, pendente, deals,
      isMe: u.id === meAtual?.id,
    };
  });
}

// ===== v5 · Pós-venda (acompanhamento pós-fechamento) =====
const POSVENDA_ETAPAS = [
  { id: "producao",    label: "Produção",    icon: "package", desc: "Fabricação / separação dos itens do pedido" },
  { id: "faturamento", label: "Faturamento", icon: "file-text", desc: "Pedido faturado · NF-e emitida" },
  { id: "onboarding",  label: "Onboarding",  icon: "rocket",  desc: "Boas-vindas, kickoff e alinhamento de expectativas" },
  { id: "expedicao",   label: "Expedição",   icon: "truck",   desc: "Aguardando coleta e despacho" },
  { id: "entrega",     label: "Entrega",     icon: "send",    desc: "Produto em trânsito para o cliente" },
  { id: "instalacao",  label: "Instalação",  icon: "wrench",  desc: "Montagem e comissionamento no local" },
  { id: "fidelizacao", label: "Fidelização", icon: "heart",   desc: "Cliente satisfeito, pronto para recompra/indicação" },
];
const POSVENDA_ETAPAS_BY_ID = Object.fromEntries(POSVENDA_ETAPAS.map(e => [e.id, e]));

// Estado inicial vazio — a página deriva defaults determinísticos por negócio
// e o gestor/vendedor sobrescreve via store.updatePosVenda.
const POSVENDA_SEED = {};

// Default determinístico de pós-venda para um negócio ganho (sem precisar de id fixo).
// meAtual = usuário autenticado da sessão (store.me) — fallback de responsável
// quando o negócio ainda não tem owner definido.
function defaultPosVenda(lead, meAtual) {
  const n = parseInt(String(lead.id).replace(/\D/g, ""), 10) || 0;
  const etapaIdx = n % POSVENDA_ETAPAS.length;
  const diasDesde = 3 + (n % 40);
  const npsPool = [9, 10, 8, 7, 9, 10, 6];
  const nps = npsPool[n % npsPool.length];
  return {
    etapa: POSVENDA_ETAPAS[etapaIdx].id,
    diasDesde,
    nps,
    responsavelId: lead.ownerId || meAtual?.id,
    proximaAcao: ["Ligar para feedback", "Sugerir reunião técnica de instalação", "Enviar pesquisa NPS", "Confirmar recebimento", "Propor recompra"][n % 5],
  };
}

// ===== v5 · Metas por papel =====
// Cada papel é cobrado por métricas diferentes.
// Chaves em minúsculas de propósito: seguem o enum `papel_usuario` do banco
// (admin, closer, bdr, sdr, backoffice), que é a fonte de verdade para
// `store.users[].papel`. Não capitalizar — quebra o lookup em gestao.jsx.
const ROLE_METAS = {
  sdr:    [{ key: "reunioesAgendadas", label: "Reuniões agendadas", unit: "reuniões", icon: "calendar" }],
  bdr:    [{ key: "reunioesAgendadas", label: "Reuniões agendadas", unit: "reuniões", icon: "calendar" }],
  closer: [
    { key: "valor",              label: "Faturamento",         money: true, icon: "wallet" },
    { key: "reunioesRealizadas", label: "Reuniões realizadas",  unit: "reuniões", icon: "check-circle-2" },
    { key: "visitas",            label: "Visitas realizadas",   unit: "visitas", icon: "compass" },
  ],
  admin:  [
    { key: "valor",              label: "Faturamento",         money: true, icon: "wallet" },
    { key: "reunioesRealizadas", label: "Reuniões realizadas",  unit: "reuniões", icon: "check-circle-2" },
    { key: "visitas",            label: "Visitas realizadas",   unit: "visitas", icon: "compass" },
  ],
  backoffice: [],
};
const AGENT_META = { key: "leadsEnriquecidos", label: "Leads gerados c/ enriquecimento", unit: "leads", icon: "cpu" };

// Realizado por métrica. Valor é real (GANHO do dono); contagens são determinísticas e plausíveis.
// Integracao de agentes externos (tokens, API de lancamento/pre-qualificacao).
// DESLIGADA desde 23/09 (Kaio, opcao 3): nao ha porta de entrada -- nenhuma Edge
// Function recebe o agente e chama api.lead_criar. Liga quando a porta existir.
// Spec: docs/superpowers/specs/2026-09-18-tokens-de-agente.md
const INTEGRACAO_DE_AGENTES = false;

// Duracao padrao por tipo de tarefa (minutos). So sugestao: o formulario
// pre-preenche o fim com ela e o vendedor ajusta; o banco so exige fim > inicio.
// Uma ligacao nao ocupa o mesmo bloco de agenda que uma visita tecnica.
const DURACAO_TAREFA_MIN = {
  "Ligação agendada": 15, "Follow-up": 15,
  "Envio de proposta": 30, "Renegociar": 30, "Liberar NF": 30,
  "Reunião": 60, "Visita técnica": 240,
};
const duracaoDoTipo = (tipo) => DURACAO_TAREFA_MIN[tipo] ?? 60;
// Opcoes do seletor de duracao (minutos).
const DURACOES_TAREFA = [15, 30, 60, 120, 240, 480];

// Negocio GANHO dentro do mes "AAAA-MM", pela data em que foi ganho (`ganhoEm`,
// gravada por gatilho na 0064). Sem data de ganho, nao entra em mes nenhum:
// nao pode inflar um mes arbitrario. E a regra "mes por ganho" (Kaio, 22/09),
// num lugar so -- relatorio, dashboard, modal de meta e Gestao usam esta.
function ganhoNoMes(lead, monthId) {
  return !!monthId && !!lead && lead.stage === "GANHO" && !lead.perdido
    && mesLocal(lead.ganhoEm) === monthId;
}

// "AAAA-MM" de uma data, no HORARIO LOCAL de quem usa. O banco devolve ISO em
// UTC; recortar a string ("2026-10-01T01:00Z".slice(0,7)) poria no mes
// SEGUINTE tudo o que aconteceu depois das 21h do ultimo dia do mes no Brasil
// (UTC-3) -- um fechamento da noite do dia 30 contaria em outubro.
function mesLocal(data) {
  if (!data) return null;
  const d = data instanceof Date ? data : new Date(data);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Ritmo do mes: quanto da meta DEVERIA estar realizado ate hoje para fechar o
// mes na meta (dias corridos / dias do mes). Mes encerrado: 100%. Mes futuro: 0%.
// Substitui a marca fixa em 60% ("Marca esperada para o dia 26") e o "faltam 5
// dias uteis" que apareciam iguais em qualquer mes e qualquer dia.
function ritmoDoMes(monthId, hoje = new Date()) {
  const idHoje = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const alvo = monthId || idHoje;
  const [ano, mes] = alvo.split("-").map(Number);
  const diasNoMes = new Date(ano, mes, 0).getDate();
  if (alvo === idHoje) {
    const dia = hoje.getDate();
    return { corrente: true, ritmo: Math.round((dia / diasNoMes) * 100), dia, diasNoMes, diasRestantes: diasNoMes - dia };
  }
  const encerrado = alvo < idHoje;
  return { corrente: false, ritmo: encerrado ? 100 : 0, dia: encerrado ? diasNoMes : 0, diasNoMes, diasRestantes: encerrado ? 0 : diasNoMes };
}

// Realizado de uma meta individual. `monthId` conta so os ganhos daquele mes --
// a meta e mensal, e comparar com todos os ganhos de sempre inflava o % a cada
// mes que passava.
function realizadoMeta(userId, campo, metaVal, leads, monthId = null) {
  if (campo === "valor") {
    return leads
      .filter(l => l.ownerId === userId && (monthId ? ganhoNoMes(l, monthId) : (l.stage === "GANHO" && !l.perdido)))
      .reduce((s, l) => s + (l.valor || 0), 0);
  }
  // Contagens (reuniões/visitas) ainda sem fonte de dados — iniciam zeradas no lançamento.
  return 0;
}

// Telefone → dígitos E.164 (o que o WhatsApp exige). Aceita número já internacional
// (+351, +1…) ou brasileiro sem DDI; devolve null quando falta DDD.
const DDI_OPCOES = [
  { code: "55",  label: "+55 BR",  nome: "Brasil" },
  { code: "1",   label: "+1 US",   nome: "EUA / Canadá" },
  { code: "351", label: "+351 PT", nome: "Portugal" },
  { code: "34",  label: "+34 ES",  nome: "Espanha" },
  { code: "39",  label: "+39 IT",  nome: "Itália" },
  { code: "44",  label: "+44 UK",  nome: "Reino Unido" },
  { code: "86",  label: "+86 CN",  nome: "China" },
];
function telE164(telefone) {
  const raw = String(telefone || "");
  // Só é estrangeiro quando o próprio número foi escrito com DDI (+34 …, 0034 …).
  const comDdi = /^\s*(\+|00)/.test(raw);
  let d = raw.replace(/\D/g, "").replace(/^00/, "");
  if (!d) return null;
  if (d.startsWith("55") && d.length >= 12) return d;                 // já tem DDI do Brasil
  if (comDdi) {
    const intl = DDI_OPCOES.some(o => o.code !== "55" && d.startsWith(o.code) && d.length >= o.code.length + 8);
    if (intl) return d;                                               // número estrangeiro
  }
  d = d.replace(/^0+/, "");                                           // 0 de tronco/operadora
  if (d.length < 10) return null;                                     // sem DDD não dá para discar
  return "55" + d;
}
// WhatsApp: monta link wa.me a partir de um telefone.
function waLink(telefone, texto) {
  const full = telE164(telefone);
  if (!full) return null;
  return "https://wa.me/" + full + (texto ? "?text=" + encodeURIComponent(texto) : "");
}

export {
  // ===== v4: Meses (filtro) + Comissões por usuário =====
  // Funis
  FUNNELS, STAGES, STAGES_PROJETOS, STAGES_LOJISTAS, STAGES_BY_FUNNEL, STAGE_NAMES,
  TIPO_CLIENTE, TIPO_CLIENTE_BY_ID,
  // Entidades
  AGENTS, AGENT_CHANNELS, AGENT_COLORS, USERS, LEADS, ALERTS, CLIENTES, GMAIL_THREADS,
  WEBHOOKS, API_LOGS,
  // v3: nichos, produtos, anexos
  NICHOS, NICHOS_BY_ID, PRODUTOS, PRODUTOS_BY_ID,
  ANEXOS, MIME_BY_EXT, MAX_ATTACHMENT_SIZE_MB,
  recomendacaoParaCliente,
  maskCNPJ, maskCPF, maskCEP, validateCNPJ, validateCPF, fiscalOk, brlExato,
  DIAS_INATIVIDADE, parseLastBuyDias, statusCarteira,
  // Relatórios
  COHORT, FUNNEL_HEALTH, ATTRIBUTION, FUNNEL_COUNTS, FUNNEL_COUNTS_PROJETOS, FUNNEL_COUNTS_LOJISTAS,
  INSIGHTS,
  // v4: meses + comissões
  MONTHS, COMMISSION_RATES, getCommissionLedger,
  // v5: pós-venda
  POSVENDA_ETAPAS, POSVENDA_ETAPAS_BY_ID, POSVENDA_SEED, defaultPosVenda,
  // v5: metas por papel + whatsapp
  ROLE_METAS, AGENT_META, realizadoMeta, ganhoNoMes, mesLocal, ritmoDoMes, waLink, telE164, DDI_OPCOES,
  DURACAO_TAREFA_MIN, duracaoDoTipo, DURACOES_TAREFA,
  INTEGRACAO_DE_AGENTES,
  // Constantes
  LOST_REASONS, TODAY,
  // Helpers
  brl, brlCompact, formatAlertWhen,
  // Vazamento lexico (Classe 2, task-8): dados de negocio nunca publicados em window
  EMISSORAS, EMISSORAS_BY_ID, AVISO_PROPOSTA, CONDICOES_PADRAO,
  FORMAS_PAGAMENTO_LIST, FORMAS_PAGAMENTO_BY_ID, FORMAS_PAGAMENTO,
  TRANSPORTE_MODALIDADES, TRANSPORTE_BY_ID, TRANSPORTADORAS_CADASTRO, TRANSPORTADORA_BY_ID,
  SERVICOS_ENTREGA, TIPOS_REDESPACHO, INTEGRACOES_LOGISTICA, METODOS_RATEIO, UFS,
  isoDia, dataDoPrazo, dataBR, nomusDataHora, nomusData, nomusNum, nomusValor,
  FAMILIAS, FAMILIAS_BY_ID, GRUPO_TO_FAMILIA, TIPO_VENDA_OPCOES, tipoVendaSugerido,
  CONDICOES_PAGAMENTO_LIST, ERP_CONFIG, buildErpClientePayload,
};

// =====================================================================
// ===== v8 · SAC — Módulo de Atendimento ao Cliente (PDLED 2026) =====
// SLA por TIPO de ocorrência (não por prioridade). SLA pausa em status de espera.
// Ciclo padrão: Aberto → Em Atendimento → Aguardando Cliente → Resolvido → Fechado
// Ciclo defeito: + Aguardando Retorno → Produto Recebido → Laude Emitido → Em Correção

const SAC_TIPOS = [
  { id: "suporte",       label: "Suporte Técnico",             short: "Suporte", icon: "wrench",       color: "var(--color-accent)", tone: "var(--color-accent-soft)", slaH: 48, temLaude: false },
  { id: "defeito",       label: "Devolução / Troca / Defeito", short: "Defeito", icon: "shield-check", color: "var(--color-danger)", tone: "var(--color-danger-soft)", slaH: 24, temLaude: true  },
  { id: "status_pedido",      label: "Status de Pedido",      short: "Pedido",  icon: "package",      color: "var(--color-warn)",   tone: "var(--color-warn-soft)",   slaH: 4,  temLaude: false },
  { id: "divergencia_pedido", label: "Divergência de Pedido", short: "Diverg.", icon: "alert-circle", color: "var(--color-corten)", tone: "var(--color-corten-soft)", slaH: 24, temLaude: true  },
];
const SAC_TIPOS_BY_ID = Object.fromEntries(SAC_TIPOS.map(t => [t.id, t]));

// Playbook por tipo — checklist + template de resolução + flag de laude
const SAC_PLAYBOOK = {
  suporte: {
    passos: [
      "Entender o cenário técnico do cliente",
      "Levantar esquema / material com o time técnico",
      "Enviar orientação (guia, vídeo ou passo a passo)",
      "Confirmar que a dúvida foi sanada",
    ],
    template: "Orientação técnica enviada e dúvida sanada com o cliente.",
    usaLaude: false,
  },
  defeito: {
    passos: [
      "Confirmar dados do pedido e produto com defeito",
      "Solicitar fotos / vídeo do defeito e nº de lote",
      "Definir se precisa retorno do produto para laude técnico",
      "Comunicar ao cliente as próximas etapas",
    ],
    template: "Defeito confirmado e tratado conforme laude técnico. Ação corretiva aplicada.",
    usaLaude: true,
  },
  status_pedido: {
    passos: [
      "Verificar situação do pedido no ERP / logística",
      "Confirmar previsão de entrega ou despacho",
      "Comunicar status atualizado ao cliente",
    ],
    template: "Status do pedido confirmado e comunicado ao cliente.",
    usaLaude: false,
  },
  divergencia_pedido: {
    passos: [
      "Confirmar número do pedido Nomus com o cliente",
      "Verificar itens recebidos vs. itens faturados no CP",
      "Solicitar fotos dos produtos recebidos",
      "Registrar divergência no Nomus ERP",
      "Acionar produção para reposição ou ajuste",
      "Emitir laude técnico com fotos e descrição da divergência",
      "Confirmar resolução com o cliente",
    ],
    template: "Divergência confirmada entre o pedido faturado e o material entregue. Reposição/ajuste acionado conforme laude técnico.",
    usaLaude: true,
  },
};

// Todos os status possíveis (union dos dois ciclos de vida)
// slaPaused: true → contador de SLA não avança neste estado
const SAC_STATUS = [
  { id: "aberto",             label: "Aberto",                       icon: "life-buoy",      desc: "Registrado, aguardando triagem",              slaPaused: false },
  { id: "em_atendimento",     label: "Em Atendimento",               icon: "wrench",         desc: "Equipe PDLED analisando",                     slaPaused: false },
  { id: "aguardando_cliente", label: "Aguardando Cliente",           icon: "clock",          desc: "Depende de retorno do cliente · SLA pausado", slaPaused: true  },
  { id: "aguardando_retorno", label: "Aguard. Retorno do Produto",   icon: "truck",          desc: "Produto a caminho · SLA pausado",             slaPaused: true  },
  { id: "produto_recebido",   label: "Produto Recebido / Ag. Laude", icon: "package",        desc: "Produto chegou, laude técnico pendente",      slaPaused: false },
  { id: "laude_emitido",      label: "Laude Emitido",                icon: "file-text",      desc: "Laude técnico emitido, ação definida",        slaPaused: false },
  { id: "em_correcao",        label: "Em Correção",                  icon: "settings",       desc: "Ação corretiva em andamento",                 slaPaused: false },
  { id: "resolvido",          label: "Resolvido",                    icon: "check-circle-2", desc: "Solução entregue, satisfação medida (CSAT)", slaPaused: false },
  { id: "fechado",            label: "Fechado",                      icon: "x-circle",       desc: "Encerrado após CSAT ou 48h sem resposta",    slaPaused: false },
];
const SAC_STATUS_BY_ID = Object.fromEntries(SAC_STATUS.map(s => [s.id, s]));

// Sequências ordenadas por tipo (para stepper e avanço automático)
const SAC_STATUS_STANDARD = ["aberto","em_atendimento","aguardando_cliente","resolvido","fechado"];
const SAC_STATUS_DEFEITO   = ["aberto","em_atendimento","aguardando_retorno","produto_recebido","laude_emitido","em_correcao","resolvido","fechado"];
function sacStatusParaTipo(tipo) {
  const seq = (tipo === "defeito" || tipo === "divergencia_pedido") ? SAC_STATUS_DEFEITO : SAC_STATUS_STANDARD;
  return seq.map(id => SAC_STATUS_BY_ID[id]);
}

// Prioridade — visual / triagem (não define o SLA)
const SAC_PRIORIDADES = [
  { id: "alta",  label: "Alta",  color: "var(--color-danger)",   tone: "var(--color-danger-soft)" },
  { id: "media", label: "Média", color: "var(--color-warn)",     tone: "var(--color-warn-soft)" },
  { id: "baixa", label: "Baixa", color: "var(--color-ink-soft)", tone: "var(--color-surface-2)" },
];
const SAC_PRIORIDADES_BY_ID = Object.fromEntries(SAC_PRIORIDADES.map(p => [p.id, p]));

// Templates de mensagem para transições de status
const SAC_TEMPLATES = {
  aguardando_retorno: (proto) =>
`Olá! Para avançarmos com a análise técnica do chamado ${proto}, precisamos que o produto seja enviado de volta.

📦 Endereço para retorno:
PDLED Iluminação — Rua das Luminárias, 480, Galpão B
São Paulo – SP · CEP 04520-000

Embale com proteção adequada e nos informe o código de rastreio após o envio. Qualquer dúvida, estamos à disposição!`,
  produto_recebido: (proto) =>
`Olá! Informamos que recebemos o produto referente ao chamado ${proto} e ele já está em análise pela nossa equipe técnica.

Assim que o laude for concluído, entraremos em contato com a definição e o prazo da ação corretiva. Obrigado pela colaboração!`,
  survey: (proto) => `https://sac.pdled.com.br/avaliacao?ref=${proto}`,
  // Mensagens de abertura por tipo de chamado (WhatsApp)
  abertura_suporte: (cliente, protocolo) =>
    `Olá, *${cliente}*! Recebemos seu chamado de suporte técnico. Protocolo: *${protocolo}*. Nossa equipe retornará em até 48h. 🔧`,
  abertura_defeito: (cliente, protocolo) =>
    `Olá, *${cliente}*! Seu chamado de defeito foi registrado. Protocolo: *${protocolo}*. Precisaremos que o produto seja enviado para análise. Aguarde nossas instruções. ⚠️`,
  abertura_divergencia: (cliente, protocolo) =>
    `Olá, *${cliente}*! Recebemos sua reclamação sobre divergência no pedido. Protocolo: *${protocolo}*. Por favor, tire fotos dos produtos recebidos e nos envie. Retornaremos em 24h. 📋`,
  abertura_status_pedido: (cliente, protocolo) =>
    `Olá, *${cliente}*! Recebemos sua consulta sobre o pedido. Protocolo: *${protocolo}*. Nossa equipe verificará e retornará em breve. 📦`,
};

const SAC_CANAIS = [
  { id: "whatsapp", label: "WhatsApp", icon: "message-circle" },
  { id: "email",    label: "E-mail",   icon: "mail" },
  { id: "telefone", label: "Telefone", icon: "phone" },
];
const SAC_CANAIS_BY_ID = Object.fromEntries(SAC_CANAIS.map(c => [c.id, c]));

// "há Xh" / "há N dias" a partir de uma quantidade de horas
function sacHorasAtras(hrs) {
  if (hrs == null) return "—";
  if (hrs < 1) return "agora";
  if (hrs < 24) return `há ${Math.round(hrs)}h`;
  const d = Math.round(hrs / 24);
  return `há ${d} dia${d > 1 ? "s" : ""}`;
}
function sacFmtHoras(h) {
  if (h == null) return "—";
  const abs = Math.abs(Math.round(h));
  if (abs < 24) return `${abs}h`;
  const d = Math.floor(abs / 24), r = abs % 24;
  return r ? `${d}d ${r}h` : `${d}d`;
}

// SLA por TIPO; pausa quando status.slaPaused=true; slaOffsetMs acumula o tempo pausado
// status retornado: resolvido | pausado | ok | risco | estourado
function sacSla(ch, now) {
  now = now || TODAY;
  const tipo = SAC_TIPOS_BY_ID[ch.tipo];
  const slaH = tipo ? tipo.slaH : 48;
  const statusDef = SAC_STATUS_BY_ID[ch.status];
  const pausado = !!(statusDef && statusDef.slaPaused);

  if (ch.status === "resolvido" || ch.status === "fechado") {
    const tmrH = ch.resolvidoEm ? (ch.resolvidoEm - ch.abertoEm) / 3600e3 : 0;
    return { status: "resolvido", restanteH: 0, slaH, pausado: false, tmrH };
  }

  // Prazo = abertura + slaH + tempo pausado acumulado
  const offsetMs = ch.slaOffsetMs || 0;
  const prazo = new Date(ch.abertoEm.getTime() + slaH * 3600e3 + offsetMs);
  const restanteH = (prazo - now) / 3600e3;

  if (pausado) return { status: "pausado", prazo, restanteH, slaH, pausado: true };

  const pctConsumido = 1 - (restanteH / slaH);
  let status = "ok";
  if (restanteH < 0) status = "estourado";
  else if (pctConsumido >= 0.75) status = "risco";
  return { status, prazo, restanteH, slaH, pausado: false };
}

// Seeds — abertoHrs/whenHrs = horas antes de TODAY (ancora na data fixa do mock)
const SAC_SEEDS = [];

// meAtual = usuário autenticado da sessão (store.me) — só entra na resolução do
// nome do autor quando a interação não é do cliente (humano/agente da equipe).
function buildChamadoInteracao(it, i, chId, meAtual) {
  const all = [meAtual, ...USERS].filter(Boolean);
  const autor = it.autorTipo === "cliente" ? "Cliente" : (all.find(u => u.id === it.autorId)?.nome || "Equipe SAC");
  return {
    id: `int_${chId}_${i}`,
    autorTipo: it.autorTipo,       // cliente | humano | agente
    autorId: it.autorId || null,
    autor,
    canal: it.canal || "sistema",
    texto: it.texto,
    whenHrs: it.whenHrs,
    when: sacHorasAtras(it.whenHrs),
  };
}

const CHAMADOS = SAC_SEEDS.map((s, i) => {
  const id = "ch_" + (4000 + i);
  const protocolo = `SAC-2026-${String(i + 1).padStart(4, "0")}`;
  const abertoEm = new Date(TODAY.getTime() - (s.abertoHrs || 0) * 3600e3);
  const isEncerrado = s.status === "resolvido" || s.status === "fechado";
  return {
    id, protocolo,
    clienteId: s.clienteId,
    leadId: s.leadId || null,
    especificadorId: s.especificadorId || null,
    produtoId: s.produtoId || null,
    tipo: s.tipo,
    canal: s.canal,
    prioridade: s.prioridade,
    status: s.status,
    responsavelId: s.responsavelId,
    assunto: s.assunto,
    descricao: s.descricao,
    abertoEm,
    abertoHrs: s.abertoHrs || 0,
    abertoQuando: sacHorasAtras(s.abertoHrs || 0),
    resolvidoEm: s.resolvidoHrs != null ? new Date(TODAY.getTime() - s.resolvidoHrs * 3600e3) : null,
    resolucao: s.resolucao || null,
    csat: s.csat != null ? s.csat : null,
    reaberturas: s.reaberturas || 0,
    passosDone: s.passosDone || (isEncerrado ? (SAC_PLAYBOOK[s.tipo]?.passos || []).map((_, i) => i) : []),
    interacoes: (s.interacoes || []).map((it, k) => buildChamadoInteracao(it, k, id)),
    slaOffsetMs: s.slaOffsetMs || 0,
    slaPausadoEm: null,
    laude: s.laude || null,
    pedidoNomus: s.pedidoNomus || null,
  };
});

export {
  SAC_TIPOS, SAC_TIPOS_BY_ID, SAC_PLAYBOOK,
  SAC_STATUS, SAC_STATUS_BY_ID, SAC_STATUS_STANDARD, SAC_STATUS_DEFEITO, sacStatusParaTipo,
  SAC_PRIORIDADES, SAC_PRIORIDADES_BY_ID, SAC_CANAIS, SAC_CANAIS_BY_ID,
  SAC_TEMPLATES,
  CHAMADOS, sacHorasAtras, sacFmtHoras, sacSla, buildChamadoInteracao,
};
